import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Sparkles, AlertTriangle, Download, Mail, RefreshCw } from "lucide-react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { useCurrency } from "../contexts/CurrencyContext";
import CityAutocomplete from "../components/CityAutocomplete";
import AstroMarkdown from "../components/AstroMarkdown";
import MatchUnlockModal from "../components/MatchUnlockModal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const label = { color: "rgba(255,255,255,0.5)", fontSize: "0.72rem", display: "block", marginBottom: "5px" };

const blankPartner = () => ({
  name: "", year: new Date().getFullYear() - 28, month: 1, day: 1,
  hour12: 12, minute: 0, period: "PM", city: "",
});

const to24 = (p) => (p.period === "PM" ? (p.hour12 % 12) + 12 : p.hour12 % 12);

const PartnerForm = ({ title, value, onChange, testPrefix, savedCharts = [], onPickSaved }) => (
  <div className="glass-card" style={{ padding: "18px", border: "1px solid rgba(255,209,102,0.14)" }}>
    <h3 className="font-cinzel" style={{ color: "#FFD166", fontSize: "0.95rem", marginBottom: "12px" }}>{title}</h3>
    {savedCharts.length > 0 && (
      <select data-testid={`${testPrefix}-saved-select`} className="cosmic-input"
        style={{ marginBottom: "10px", fontSize: "0.8rem" }} defaultValue=""
        onChange={(e) => e.target.value && onPickSaved(e.target.value)}>
        <option value="">Use a saved Kundli…</option>
        {savedCharts.map(c => <option key={c.id} value={c.id} label={`${c.name} · ${c.dob}`} />)}
      </select>
    )}
    <input data-testid={`${testPrefix}-name`} value={value.name} onChange={e => onChange({ ...value, name: e.target.value })}
      placeholder="Full name" className="cosmic-input" required style={{ marginBottom: "10px" }} />
    <label style={label}>Date of birth</label>
    <div className="birth-grid-3" style={{ marginBottom: "10px" }}>
      <input data-testid={`${testPrefix}-day`} type="number" min={1} max={31} value={value.day}
        onChange={e => onChange({ ...value, day: parseInt(e.target.value) })} className="cosmic-input" required />
      <select data-testid={`${testPrefix}-month`} value={value.month}
        onChange={e => onChange({ ...value, month: parseInt(e.target.value) })} className="cosmic-input">
        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
      <input data-testid={`${testPrefix}-year`} type="number" min={1920} max={2026} value={value.year}
        onChange={e => onChange({ ...value, year: parseInt(e.target.value) })} className="cosmic-input" required />
    </div>
    <label style={label}>Time of birth</label>
    <div className="birth-grid-3" style={{ marginBottom: "10px" }}>
      <select data-testid={`${testPrefix}-hour`} value={value.hour12}
        onChange={e => onChange({ ...value, hour12: parseInt(e.target.value) })} className="cosmic-input">
        {Array.from({ length: 12 }, (_, i) => i + 1).map(h => <option key={h} value={h}>{String(h).padStart(2, "0")}</option>)}
      </select>
      <select data-testid={`${testPrefix}-minute`} value={value.minute}
        onChange={e => onChange({ ...value, minute: parseInt(e.target.value) })} className="cosmic-input">
        {Array.from({ length: 60 }, (_, i) => i).map(m => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
      </select>
      <select data-testid={`${testPrefix}-period`} value={value.period}
        onChange={e => onChange({ ...value, period: e.target.value })} className="cosmic-input">
        <option value="AM">AM</option><option value="PM">PM</option>
      </select>
    </div>
    <label style={label}>Birth city</label>
    <CityAutocomplete value={value.city} onChange={(v) => onChange({ ...value, city: v })} required name={`${testPrefix}-city`} />
  </div>
);

export default function MatchingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const { price } = useCurrency();
  const [p1, setP1] = useState(blankPartner());
  const [p2, setP2] = useState(blankPartner());
  const [savedCharts, setSavedCharts] = useState([]);
  const [score, setScore] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [showUnlock, setShowUnlock] = useState(false);
  const [report, setReport] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState("");

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/kundli/charts`, { withCredentials: true })
      .then(r => setSavedCharts(r.data?.charts || []))
      .catch(() => {});
  }, [user]);

  const pickSaved = async (id) => {
    try {
      const { data } = await axios.get(`${API}/kundli/chart/${id}`, { withCredentials: true });
      const bd = data?.birth_details || {};
      setP1({
        name: data?.name || bd.name || "", year: bd.year || 1995, month: bd.month || 1, day: bd.day || 1,
        hour12: bd.hour12 || ((bd.hour ?? 12) % 12 || 12), minute: bd.minute ?? 0,
        period: bd.period || ((bd.hour ?? 12) < 12 ? "AM" : "PM"), city: bd.city || "",
      });
    } catch {}
  };

  const check = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/auth?redirect=/matching"); return; }
    setError(""); setChecking(true); setScore(null); setReport(null);
    try {
      const body = {
        partner1: { name: p1.name, year: p1.year, month: p1.month, day: p1.day, hour: to24(p1), minute: p1.minute, city: p1.city },
        partner2: { name: p2.name, year: p2.year, month: p2.month, day: p2.day, hour: to24(p2), minute: p2.minute, city: p2.city },
        lang,
      };
      const { data } = await axios.post(`${API}/match/score`, body, { withCredentials: true, timeout: 180000 });
      setScore(data);
    } catch (err) {
      setError(err.response?.data?.detail || t("match.failed"));
    } finally { setChecking(false); }
  };

  const generateReport = async () => {
    if (!score?.check_id) return;
    setReportBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/match/report`,
        { check_id: score.check_id, lang }, { withCredentials: true, timeout: 180000 });
      setReport(data);
      setScore(s => ({ ...s, credits: data.credits, can_unlock: true }));
    } catch (err) {
      if (err.response?.status === 402) setShowUnlock(true);
      else setError(err.response?.data?.detail || t("match.failed"));
    } finally { setReportBusy(false); }
  };

  const downloadPdf = async () => {
    setActionMsg("");
    try {
      const res = await axios.get(`${API}/match/report/${report.report_id}/pdf`, { responseType: "blob", withCredentials: true });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `AstroFate_Match_${p1.name || "report"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 20000);
    } catch { setActionMsg(t("glance.pdfFailed")); }
  };

  const emailPdf = async () => {
    setActionMsg("");
    try {
      const { data } = await axios.post(`${API}/match/report/${report.report_id}/email`, null, { withCredentials: true, timeout: 120000 });
      setActionMsg(`${t("pdf.emailed")} ${data.email}`);
    } catch { setActionMsg(t("pdf.emailFailed")); }
  };

  const toneColor = { excellent: "#86efac", very_good: "#a3e635", acceptable: "#FFD166", weak: "#fca5a5" };

  return (
    <div style={{ minHeight: "100vh", background: "#120021", paddingTop: "84px" }}>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", width: "520px", height: "520px", background: "radial-gradient(circle, rgba(249,168,212,0.10), transparent)", top: "-90px", left: "-60px", borderRadius: "50%" }} />
        <div style={{ position: "absolute", width: "440px", height: "440px", background: "radial-gradient(circle, rgba(91,30,140,0.28), transparent)", bottom: 0, right: "-70px", borderRadius: "50%" }} />
      </div>

      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "0 16px 70px", position: "relative", zIndex: 1 }}>
        <div className="animate-fadeInUp" style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Heart size={22} style={{ color: "#f9a8d4" }} />
            <h1 className="font-cinzel" style={{ fontSize: "clamp(1.5rem, 4vw, 2rem)", color: "#FFD166" }}>{t("match.title")}</h1>
          </div>
          <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.86rem", marginTop: "4px" }}>{t("match.subtitle", { price: price("match") })}</p>
        </div>

        {!score && (
          <form onSubmit={check} data-testid="match-form">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "16px", marginBottom: "18px" }}>
              <PartnerForm title={t("match.partner1")} value={p1} onChange={setP1} testPrefix="match-p1"
                savedCharts={savedCharts} onPickSaved={pickSaved} />
              <PartnerForm title={t("match.partner2")} value={p2} onChange={setP2} testPrefix="match-p2" />
            </div>
            {error && <div data-testid="match-error" style={{ color: "#fca5a5", fontSize: "0.84rem", marginBottom: "12px" }}>{error}</div>}
            <button data-testid="match-check-btn" type="submit" disabled={checking} className="btn-gold"
              style={{ width: "100%", fontSize: "0.98rem", opacity: checking ? 0.6 : 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              {checking ? (
                <>
                  <span className="cosmic-spinner" style={{ width: "16px", height: "16px", border: "2px solid rgba(18,0,33,0.3)", borderTop: "2px solid #120021" }} />
                  {t("match.checking")}
                </>
              ) : `✦ ${t("match.checkBtn")} ✦`}
            </button>
          </form>
        )}

        {score && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div className="glass-card animate-fadeInUp" data-testid="match-score-card"
              style={{ padding: "24px", border: "1px solid rgba(255,209,102,0.2)", background: "linear-gradient(135deg, rgba(91,30,140,0.2), rgba(45,11,69,0.32))" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                <div style={{ textAlign: "center" }}>
                  <div data-testid="match-total" style={{ fontSize: "2.6rem", fontWeight: 700, color: toneColor[score.tone] || "#FFD166", lineHeight: 1 }}>
                    {score.total}
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.74rem" }}>{t("match.scoreOf")}</div>
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div data-testid="match-verdict" className="font-cinzel" style={{ color: toneColor[score.tone] || "#FFD166", fontSize: "1.1rem" }}>
                    {score.verdict}
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.82rem", marginTop: "3px" }}>
                    {score.boy?.moon_sign} ({score.boy?.nakshatra}) &nbsp;·&nbsp; {score.girl?.moon_sign} ({score.girl?.nakshatra})
                  </div>
                  <div style={{ height: "7px", borderRadius: "50px", background: "rgba(255,255,255,0.08)", marginTop: "10px", overflow: "hidden" }}>
                    <div style={{ width: `${score.percent}%`, height: "100%", background: `linear-gradient(90deg, #9D4EDD, ${toneColor[score.tone] || "#FFD166"})`, transition: "width 700ms ease" }} />
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "14px" }}>
                {(score.doshas || []).length === 0 ? (
                  <span data-testid="match-no-doshas" style={{ fontSize: "0.74rem", padding: "4px 10px", borderRadius: "50px", background: "rgba(134,239,172,0.12)", color: "#86efac", border: "1px solid rgba(134,239,172,0.3)" }}>
                    ✓ {t("match.noDoshas")}
                  </span>
                ) : score.doshas.map(d => (
                  <span key={d} data-testid="match-dosha-chip" style={{ fontSize: "0.74rem", padding: "4px 10px", borderRadius: "50px", background: "rgba(239,68,68,0.1)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.28)", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                    <AlertTriangle size={11} /> {d}
                  </span>
                ))}
                {["partner1", "partner2"].map(k => score.manglik?.[k] && (
                  <span key={k} style={{ fontSize: "0.74rem", padding: "4px 10px", borderRadius: "50px", background: "rgba(249,115,22,0.1)", color: "#fdba74", border: "1px solid rgba(249,115,22,0.28)" }}>
                    {t("match.manglik")}: {k === "partner1" ? p1.name || "P1" : p2.name || "P2"}
                  </span>
                ))}
              </div>
            </div>

            <div className="glass-card" style={{ padding: "18px" }}>
              <h3 className="font-cinzel" style={{ color: "#FFD166", fontSize: "0.92rem", marginBottom: "12px" }}>{t("match.kootaTable")}</h3>
              <table data-testid="match-koota-table" className="koota-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                <thead>
                  <tr style={{ color: "rgba(255,255,255,0.4)", textAlign: "left" }}>
                    <th style={{ padding: "6px 8px" }}>{t("match.koota")}</th>
                    <th style={{ padding: "6px 8px" }}>{t("match.points")}</th>
                    <th style={{ padding: "6px 8px" }}>{t("match.details")}</th>
                    <th style={{ padding: "6px 8px" }}>{t("match.meaning")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(score.kootas || []).map(k => (
                    <tr key={k.key} data-testid={`match-koota-${k.key}`} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                      <td style={{ padding: "8px", color: "white", fontWeight: 600 }}>{k.name}</td>
                      <td style={{ padding: "8px", color: k.score === 0 ? "#fca5a5" : "#86efac", fontWeight: 700, whiteSpace: "nowrap" }}>{k.score}/{k.max}</td>
                      <td style={{ padding: "8px", color: "rgba(255,255,255,0.65)" }}>{k.detail}</td>
                      <td style={{ padding: "8px", color: "rgba(255,255,255,0.4)" }}>{k.means}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!report && (
              <div className="glass-card" style={{ padding: "22px", textAlign: "center", border: "1px solid rgba(249,168,212,0.22)" }}>
                <Sparkles size={18} style={{ color: "#f9a8d4", marginBottom: "8px" }} />
                <h3 className="font-cinzel" style={{ color: "#FFD166", marginBottom: "6px" }}>{t("match.unlockTitle")}</h3>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.86rem", marginBottom: "16px" }}>{t("match.unlockDesc")}</p>
                <button data-testid="match-unlock-btn" onClick={score.can_unlock ? generateReport : () => setShowUnlock(true)}
                  disabled={reportBusy} className="btn-gold" style={{ opacity: reportBusy ? 0.6 : 1 }}>
                  {reportBusy ? t("match.generating") : (score.can_unlock ? t("match.generateReport") : t("match.unlockCta", { price: price("match") }))}
                </button>
                {error && <div data-testid="match-report-error" style={{ color: "#fca5a5", fontSize: "0.82rem", marginTop: "10px" }}>{error}</div>}
              </div>
            )}

            {report && (
              <div className="glass-card animate-fadeInUp" data-testid="match-report" style={{ padding: "22px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px", marginBottom: "12px" }}>
                  <h3 className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.05rem" }}>{t("match.reportTitle")}</h3>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button data-testid="match-pdf-btn" onClick={downloadPdf} className="btn-ghost"
                      style={{ padding: "7px 13px", fontSize: "0.8rem", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                      <Download size={13} /> PDF
                    </button>
                    <button data-testid="match-email-btn" onClick={emailPdf} className="btn-ghost"
                      style={{ padding: "7px 13px", fontSize: "0.8rem", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                      <Mail size={13} /> Email
                    </button>
                  </div>
                </div>
                {actionMsg && <div data-testid="match-action-msg" style={{ color: "#86efac", fontSize: "0.8rem", marginBottom: "10px" }}>{actionMsg}</div>}
                <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "0.92rem", lineHeight: 1.75 }}>
                  <AstroMarkdown>{report.report}</AstroMarkdown>
                </div>
              </div>
            )}

            <button data-testid="match-reset-btn" onClick={() => { setScore(null); setReport(null); setError(""); }}
              className="btn-ghost" style={{ alignSelf: "flex-start", padding: "8px 14px", fontSize: "0.82rem", display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <RefreshCw size={13} /> {t("match.newCheck")}
            </button>
          </div>
        )}
      </div>

      {showUnlock && (
        <MatchUnlockModal onClose={() => setShowUnlock(false)}
          onSuccess={() => { setShowUnlock(false); generateReport(); }} />
      )}
    </div>
  );
}
